import { useState } from "react";

export default function TeamManagement({
  currentOrg,
  teamMembers,
  loading,
  inviteTeamMember,
  removeTeamMember,
  therapistLimit,
  isTrialActive,
  notify,
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("therapist");
  const [designation, setDesignation] = useState("");
  const [sending, setSending] = useState(false);

  const activeCount = teamMembers.filter((m) => m.status === "active").length;
  const limitLabel = therapistLimit === null ? "Unlimited" : `${activeCount} of ${therapistLimit}`;
  const limitReached = therapistLimit !== null && activeCount >= therapistLimit;

  async function handleInvite(e) {
    e.preventDefault();
    if (!email) return;

    setSending(true);
    try {
      await inviteTeamMember(email, role, designation || null);
      notify?.(`Invitation sent to ${email}`, "success");
      setEmail("");
      setDesignation("");
    } catch (err) {
      notify?.(err.message, "error");
    } finally {
      setSending(false);
    }
  }

  async function handleRemove(userId, name) {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    try {
      await removeTeamMember(userId);
      notify?.(`${name} removed from team`, "success");
    } catch (err) {
      notify?.(err.message, "error");
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Loading team...</div>;

  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ color: "#e8c568", marginBottom: 4 }}>Team Members</h2>
      <p style={{ color: "#999", marginBottom: 16 }}>
        {currentOrg?.name || "Your clinic"} · Therapists: {limitLabel}
        {isTrialActive && " (Free Trial — unlimited during trial)"}
      </p>

      {/* Invite Form */}
      <form
        onSubmit={handleInvite}
        style={{
          background: "#15161b",
          border: "1px solid #2a2b33",
          borderRadius: 10,
          padding: 20,
          marginBottom: 24,
        }}
      >
        <h3 style={{ marginBottom: 12 }}>Invite a Therapist</h3>

        {limitReached ? (
          <div style={{ color: "#ff6b6b", marginBottom: 12 }}>
            ⚠️ Therapist limit reached ({therapistLimit}). Upgrade your plan to invite more.
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <input
            type="email"
            placeholder="therapist@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={limitReached}
            style={{ flex: 1, minWidth: 220, padding: 10, borderRadius: 6 }}
          />

          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={limitReached}
            style={{ padding: 10, borderRadius: 6 }}
          >
            <option value="therapist">Therapist</option>
            <option value="admin">Admin</option>
          </select>

          <input
            type="text"
            placeholder="Designation (optional)"
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            disabled={limitReached}
            style={{ padding: 10, borderRadius: 6, minWidth: 160 }}
          />
        </div>

        <button
          type="submit"
          disabled={sending || limitReached}
          style={{
            background: "#e8c568",
            color: "#000",
            border: "none",
            borderRadius: 6,
            padding: "10px 20px",
            fontWeight: "bold",
            cursor: limitReached ? "not-allowed" : "pointer",
            opacity: limitReached ? 0.5 : 1,
          }}
        >
          {sending ? "Sending..." : "Send Invite"}
        </button>
      </form>

      {/* Team List */}
      <div
        style={{
          background: "#15161b",
          border: "1px solid #2a2b33",
          borderRadius: 10,
          padding: 20,
        }}
      >
        <h3 style={{ marginBottom: 12 }}>Current Team ({activeCount})</h3>

        {teamMembers.filter((m) => m.status === "active").length === 0 ? (
          <p style={{ color: "#777" }}>No team members yet. Invite your first therapist above.</p>
        ) : (
          teamMembers
            .filter((m) => m.status === "active")
            .map((member) => (
              <div
                key={member.user_id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #2a2b33",
                }}
              >
                <div>
                  <div style={{ fontWeight: "bold" }}>{member.full_name || member.email}</div>
                  <div style={{ fontSize: 13, color: "#999" }}>
                    {member.email} · {member.role}
                    {member.designation ? ` · ${member.designation}` : ""}
                  </div>
                </div>

                <button
                  onClick={() => handleRemove(member.user_id, member.full_name || member.email)}
                  style={{
                    background: "transparent",
                    color: "#ff6b6b",
                    border: "1px solid #ff6b6b",
                    borderRadius: 6,
                    padding: "6px 12px",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            ))
        )}
      </div>
    </div>
  );
}